import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { getDropZoneAcceptValidator } from '@modules/sequence/drop-zone-accept-validator';
import { MediaPickerValidator } from '@modules/shared/media-picker/models/media-picker-validator';
import { MediaPickerDialogService } from '@modules/shared/media-picker/services/media-picker-dialog.service';
import { SyncCenterStateService } from '@modules/shared/sync-center/sync-center-state.service';
import { Store } from '@ngrx/store';
import { DialogService } from '@services/dialog.service';
import { VideoUploadHttpService } from '@services/http/video-upload.http.service';
import { VideoUploadFileService } from '@services/video-upload/video-upload-file.service';
import { MediaLocalPreviewService } from '@services/media-local-preview/media-local-preview.service';
import { MAX_MEDIA_UPLOAD_SIZE } from '@shared/utils/max-media-upload-size';
import { sequencesActions } from '@shared/store/sequences/sequences.actions';
import { VideoUploadEntry } from '@shared/store/video-uploads/models/video-upload-entry';
import { VideoUploadReference } from '@shared/store/video-uploads/models/video-upload-reference';
import { videoUploadsActions } from '@shared/store/video-uploads/video-uploads.actions';
import { selectVideoUploadEntry } from '@shared/store/video-uploads/video-uploads.selectors';
import { filter, map, of, switchMap, take } from 'rxjs';

// Every entry point takes the upload's own client code — the sync-center gathers the uploads of all
// the user's clients, so the request must run against the upload's client, not the active one.
@Injectable({ providedIn: 'root' })
export class VideoUploadResumeService {
  private readonly store = inject(Store);
  private readonly mediaPickerDialog = inject(MediaPickerDialogService);
  private readonly dialogService = inject(DialogService);
  private readonly translocoService = inject(TranslocoService);
  private readonly mediaLocalPreviewService = inject(MediaLocalPreviewService);
  private readonly fileService = inject(VideoUploadFileService);
  private readonly videoUploadHttpService = inject(VideoUploadHttpService);
  private readonly syncCenterState = inject(SyncCenterStateService);

  // Opens the media picker so the user can re-select the interrupted file. The picker only lets
  // the user validate once the picked file matches the interrupted one — which is what makes it
  // safe to resume the existing session rather than restart: the chunks already stored server-side
  // belong to those exact bytes.
  resume(reference: VideoUploadReference): void {
    this.store
      .select(selectVideoUploadEntry(reference))
      .pipe(take(1))
      .subscribe((entry) => {
        if (!entry) {
          return;
        }

        this.mediaPickerDialog
          .open(
            {
              groups$: of([]),
              maxSelectable: 1,
              isLongVideoEnabled: true,
              accept: getDropZoneAcceptValidator(true),
              // Videos routinely exceed the picker's default 30 MB cap; the resume must accept the
              // same sizes the original upload did.
              maxFileSize: MAX_MEDIA_UPLOAD_SIZE,
              validators: [this.buildMatchValidator(entry)],
              subtitle: this.translocoService.translate(
                'Sélectionnez à nouveau le fichier « {{fileName}} » pour reprendre son envoi interrompu.',
                { fileName: entry.fileName },
              ),
              confirmLabel: this.translocoService.translate('Reprendre'),
            },
            { label: this.translocoService.translate('Reprendre la mise en ligne'), size: 'm' },
          )
          .subscribe((selection) => {
            const file = selection?.files[0];
            if (!file) {
              return;
            }
            // Keep the session alive and hand its token to the upload: aborting it here would
            // throw away the chunks the server already holds and force a full re-upload.
            this.startUpload(reference, entry, file);
          });
      });
  }

  // Retries a failed upload straight from the File kept in memory (an errored upload only exists in
  // a live session, so its bytes are still around). Reuses resumeVideoUpload: with a sessionToken it
  // resumes the server session, without one it restarts — the action already covers both.
  retry(reference: VideoUploadReference): void {
    this.store
      .select(selectVideoUploadEntry(reference))
      .pipe(take(1))
      .subscribe((entry) => {
        const file = this.fileService.get(reference.videoId);
        if (!entry || !file) {
          return;
        }
        this.startUpload(reference, entry, file);
      });
  }

  // Re-creates the preview object URL for the file about to be sent (an interrupted entry has none
  // left) and hands the upload its existing session token, if any.
  private startUpload(reference: VideoUploadReference, entry: VideoUploadEntry, file: File): void {
    this.store.dispatch(
      videoUploadsActions.resumeVideoUpload({
        ...reference,
        file,
        previewUrl: this.mediaLocalPreviewService.register(reference.videoId, file),
        sessionToken: entry.sessionToken,
      }),
    );
  }

  // Deletes an upload after confirmation, and in the case the user stops a live upload,
  // aborts it so the server session is discarded and the chunks already stored are dropped.
  delete(reference: VideoUploadReference): void {
    this.store
      .select(selectVideoUploadEntry(reference))
      .pipe(
        take(1),
        switchMap((entry) => {
          if (!entry) {
            return of(null);
          }
          this.syncCenterState.close();
          return this.dialogService
            .openConfirm(this.translocoService.translate("Supprimer l'envoi interrompu ?"), {
              content: this.translocoService.translate('Le fichier « {{fileName}} » ne pourra plus être repris.', {
                fileName: entry.fileName,
              }),
              yes: this.translocoService.translate('Supprimer'),
              isDestructive: true,
              no: this.translocoService.translate('Annuler'),
            })
            .pipe(
              take(1),
              filter((confirmed) => confirmed),
              map(() => entry),
            );
        }),
      )
      .subscribe((entry) => {
        if (!entry) {
          return;
        }
        // Stop the live upload if one is running (in-progress video): aborting its batch also
        // discards its server session. A hydrated/errored entry has no live upload — its dead
        // session is dropped below via its stored token instead.
        this.videoUploadHttpService.abortUpload(reference.videoId);
        if (entry.sessionToken) {
          this.store.dispatch(
            videoUploadsActions.abortInterruptedSession({
              clientCode: reference.clientCode,
              sequenceId: reference.sequenceId,
              sessionToken: entry.sessionToken,
            }),
          );
        }
        this.store.dispatch(videoUploadsActions.removeVideoUpload(reference));
        // Drop its gray square from the gallery right away instead of waiting for the next fetch.
        this.store.dispatch(
          sequencesActions.removeOptimisticMedia({
            clientCode: reference.clientCode,
            sequenceId: reference.sequenceId,
            originalFilename: entry.fileName,
            size: entry.fileSize,
          }),
        );
        // Re-open the panel closed when the confirm dialog opened, so the user lands back on it.
        this.syncCenterState.open();
      });
  }

  // A picked file matches the interrupted one when its name, size and last-modified timestamp are
  // identical — enough to be confident it is the same file the user tried to send.
  private buildMatchValidator =
    (entry: VideoUploadEntry): MediaPickerValidator =>
    (selection) => {
      const file = selection.files[0];
      if (!file) {
        return null;
      }
      const matches =
        file.name === entry.fileName &&
        file.size === entry.fileSize &&
        (entry.lastModified === undefined || file.lastModified === entry.lastModified);
      return matches ? null : this.translocoService.translate('Ce fichier ne correspond pas au fichier interrompu.');
    };
}
